import React, { useEffect, useState } from "react";
import { Copy } from "lucide-react";
import { api, inr } from "@/lib/api";
import { StockMenu, WatchAdd } from "@/components/WatchAdd";
import { copyTv } from "@/pages/VaCross";

const TFS = [["D", "Daily"], ["W", "Weekly"], ["M", "Monthly"]];
const chip = (on) => `h-7 px-2.5 text-[11px] font-mono border transition-colors ${on ? "bg-blue-600 text-white border-blue-600" : "border-slate-300 text-slate-500 hover:border-blue-500"}`;
const STAR = { 3: "bg-[#00E676] text-black", 2: "bg-[#FFD600] text-black" };

const Cell = ({ v, testid }) => {
  const s = v.now || v.last;
  if (!s) return <span className="w-44 text-center text-slate-500 text-[10px]" data-testid={testid}>{v.now_x != null ? `${v.now_x}×` : "n/a"}</span>;
  const now = !!v.now;
  const del = now ? v.now_del : v.last_del;
  return (
    <span className="w-44 text-center" data-testid={testid}>
      <span className={`text-[10px] px-1.5 py-px font-bold ${STAR[s]}`}>{s === 3 ? "GREEN" : "YELLOW"} {now ? v.now_x : v.last_x}×</span>
      {del != null && <span className={`ml-1 text-[10px] px-1 py-px font-bold border ${del >= 60 ? "border-emerald-500 text-emerald-400" : del < 30 ? "border-rose-500/60 text-rose-400" : "border-slate-400 text-slate-600"}`}
        title={`Delivery ${del}% of traded volume — ${del >= 60 ? "strong real buying/selling" : del < 30 ? "mostly intraday churn" : "mixed"}`} data-testid={`${testid}-del`}>Del {del}%</span>}
      <span className="block text-[9px] text-slate-500">{now ? "current candle" : "last candle"}</span>
    </span>
  );
};

const hit = (v, color, when) => {
  const ok = (s) => (color === "any" ? s >= 2 : s === (color === "green" ? 3 : 2));
  return (when !== "last" && ok(v.now)) || (when !== "now" && ok(v.last));
};

export const BeivPanel = () => {
  const [d, setD] = useState(null);
  const [tfs, setTfs] = useState(["D", "W", "M"]);
  const [color, setColor] = useState("any");
  const [when, setWhen] = useState("both");
  useEffect(() => {
    api.get("/today/beiv").then((r) => setD(r.data)).catch(() => setD({ rows: [] }));
  }, []);
  const rows = (d?.rows || []).filter((r) => tfs.some((t) => hit(r.tf[t], color, when)));
  const toggleTf = (t) => setTfs(tfs.includes(t) ? (tfs.length > 1 ? tfs.filter((x) => x !== t) : tfs) : [...tfs, t]);
  return (
    <div className="space-y-3" data-testid="beiv-panel">
      <div className="flex flex-wrap items-center gap-1">
        {TFS.map(([k, l]) => <button key={k} onClick={() => toggleTf(k)} aria-pressed={tfs.includes(k)} className={chip(tfs.includes(k))} data-testid={`beiv-tf-${k}`}>{l}</button>)}
        <span className="w-3" />
        {[["any", "Yellow or Green"], ["yellow", "Yellow 4×+"], ["green", "Green 6.5×+"]].map(([k, l]) => <button key={k} onClick={() => setColor(k)} className={chip(color === k)} data-testid={`beiv-color-${k}`}>{l}</button>)}
        <span className="w-3" />
        {[["both", "Current or last"], ["now", "Current candle"], ["last", "Last completed"]].map(([k, l]) => <button key={k} onClick={() => setWhen(k)} className={chip(when === k)} data-testid={`beiv-when-${k}`}>{l}</button>)}
        <button onClick={() => copyTv(rows)} data-testid="beiv-copy-tv" className="ml-auto h-7 px-3 text-xs font-mono border border-slate-300 text-slate-800 hover:border-blue-500 transition-colors flex items-center gap-1.5"><Copy className="h-3.5 w-3.5 text-blue-600" /> Copy to TradingView</button>
      </div>
      <div className="border border-slate-200 bg-white" data-testid="beiv-list">
        <div className="flex gap-2 px-2 py-1 text-[9px] uppercase text-slate-500 font-mono border-b border-slate-200">
          <span className="flex-1">Stock <span className="normal-case" data-testid="beiv-count">({rows.length})</span></span>{TFS.map(([k, l]) => <span key={k} className="w-44 text-center">{l}</span>)}<span className="w-20 text-right">Price</span><span className="w-14 text-right">Day %</span><span className="w-7" />
        </div>
        {!d ? <p className="p-4 text-xs font-mono text-slate-500">Loading…</p> : rows.length ? rows.map((r) => {
          const body = (
            <button className="flex-1 min-w-0 flex items-center gap-2 text-xs font-mono px-2 py-1.5 hover:bg-blue-500/10 transition-colors text-left" data-testid={`beiv-row-${r.symbol}`}
              onClick={r.instrument_id ? undefined : () => window.open(`https://www.tradingview.com/chart/?symbol=NSE:${r.symbol.replace(/[&-]/g, "_")}`, "_blank", "noopener")}>
              <span className="flex-1 min-w-0 text-slate-900 font-semibold">{r.symbol}
                {r.fno && <span className="ml-1 text-[9px] px-1 font-bold border border-blue-500/50 text-sky-300">F&O</span>}
                {d.hot?.top?.includes(r.sector) && <span className="ml-1 text-[9px] px-1 font-bold bg-emerald-600 text-white">TOP</span>}
                {d.hot?.bottom?.includes(r.sector) && <span className="ml-1 text-[9px] px-1 font-bold bg-rose-600 text-white">WORST</span>}
                <span className="block text-[9px] text-slate-500 font-normal">{r.sector}</span></span>
              {TFS.map(([k]) => <Cell key={k} v={r.tf[k]} testid={`beiv-${k}-${r.symbol}`} />)}
              <span className="w-20 text-right text-slate-800">{inr(r.ltp)}</span>
              <span className={`w-14 text-right ${r.change_pct >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{r.change_pct == null ? "—" : `${r.change_pct > 0 ? "+" : ""}${r.change_pct}%`}</span>
            </button>
          );
          return (
            <div key={r.symbol} className="flex items-center gap-1 border-b border-slate-200">
              {r.instrument_id ? <StockMenu instrumentId={r.instrument_id} symbol={r.symbol}>{body}</StockMenu> : body}
              {r.instrument_id ? <WatchAdd instrumentId={r.instrument_id} symbol={r.symbol} /> : <span className="w-7" />}
            </div>
          );
        }) : <p className="p-4 text-xs font-mono text-slate-500" data-testid="beiv-empty">No stock matches these filters</p>}
      </div>
      <p className="text-[10px] font-mono text-slate-500">Nifty 500 · official NSE end-of-day data (updates after the close, ~6 pm) · session {d?.session || "—"} · {d?.sessions_loaded || 0} sessions loaded · Blue Elephant Institutional Volume: candle volume vs its 20-bar average (incl. itself) · yellow = 4×+ · green = 6.5×+ · current = still-forming week / month · n/a = not enough history · non-F&O rows open TradingView</p>
    </div>
  );
};
